import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

const SELECTORS = {
  widget: '.js-a11y-widget',
  toggle: '.js-a11y-toggle',
  toolbar: '.js-a11y-toolbar'
}

class A11yWidget {
  constructor (element) {
    this.element = element
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.toolbar = this.element.querySelector(SELECTORS.toolbar)
    this.toggleBtn = this.element.querySelector(SELECTORS.toggle)
    this.contrastBtn = this.element.querySelector('[value="contrast"]')
    this.motionBtn = this.element.querySelector('[value="motion"]')
    this.forcedColorsBtn = this.element.querySelector('[value="forced-colors"]')
    this.contrastLabel = this.element.querySelector('.a11y-widget__setting--contrast')
    this.motionLabel = this.element.querySelector('.a11y-widget__setting--motion')
    this.forcedColorsLabel = this.element.querySelector('.a11y-widget__setting--forced-colors')
    this.contrastCheckbox = this.element.querySelector('[value="contrast"] input')
    this.motionCheckbox = this.element.querySelector('[value="motion"] input')
    this.forcedColorsCheckbox = this.element.querySelector('[value="forced-colors"] input')
    this.closeBtn = this.element.querySelector('[value="close"]')

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.contrastQuery = window.matchMedia('(prefers-contrast: more), (prefers-reduced-transparency: reduce)')
    this.forcedColorsQuery = window.matchMedia('(forced-colors: active)')
    this.params = new URLSearchParams(window.location.search)
    this.cookies = document.cookie
    this.observer = this.registerObserver()

    this.keyCode = Object.freeze({
      TAB: 9,
      ESC: 27,
      END: 35,
      HOME: 36,
      LEFT: 37,
      UP: 38,
      RIGHT: 39,
      DOWN: 40
    })

    this.options = {
      reduceMotion: undefined,
      increaseContrast: undefined,
      forcedColors: undefined
    }

    this.init()
  }

  init() {
    this.getInitialMotionOption()
    this.getInitialContrastOption()
    this.getInitialForcedColorsOption()
    this.setOptions()
    this.setMotionState()
    // this.setContrastState()
    // this.setForcedColorsState()

    this.registerEventHandlers()

    const uhfActions = document.querySelector('.c-uhfh-actions')
    if (uhfActions) {
      uhfActions.parentElement.insertBefore(this.element, uhfActions)
    }
  }

  registerObserver() {
    return new IntersectionObserver(elements => {
      elements.forEach(element => {
        if (!element.isIntersecting) {
          this.closeMenu(false)
        }
      })
    }, {
      rootMargin: '20px',
      threshold: 0
    })
  }

  setOptions() {
    if (this.options.reduceMotion) {
      document.documentElement.classList.add('reduced-motion')
    } else {
      document.documentElement.classList.remove('reduced-motion')
    }
    this.motionBtn.setAttribute('aria-checked', this.options.reduceMotion ? 'true' : 'false')

    if (this.options.increaseContrast) {
      document.documentElement.classList.add('increased-contrast')
    } else {
      document.documentElement.classList.remove('increased-contrast')
    }
    // this.contrastBtn.setAttribute('aria-checked', this.options.increaseContrast ? 'true' : 'false')

    if (this.options.forcedColors) {
      document.documentElement.classList.remove('forced-color-override')
    } else {
      document.documentElement.classList.add('forced-color-override')
    }
    // this.forcedColorsBtn.setAttribute('aria-checked', this.options.forcedColors ? 'true' : 'false')

    if (typeof this.options.reduceMotion !== 'undefined') {
      document.cookie = `a11y_reduce_motion=${this.options.reduceMotion}; Path=/; SameSite=Strict`
    }

    // if (typeof this.options.increaseContrast !== 'undefined') {
    //   document.cookie = `a11y_increase_contrast=${this.options.increaseContrast}; Path=/; SameSite=Strict`
    // }

    // if (typeof this.options.forcedColors !== 'undefined') {
    //   document.cookie = `a11y_forced_colors_override=${!this.options.forcedColors}; Path=/; SameSite=Strict`
    // }

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, this.options.reduceMotion)
    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.INCREASE_CONTRAST_CHANGE, this.options.increaseContrast)
    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.FORCED_COLORS_CHANGE, this.options.forcedColors)
  }

  setMotionState() {
    if (this.options.reduceMotion === true) {
      this.motionLabel.innerHTML = 'Off'
      this.motionCheckbox.checked = false
    } else {
      this.motionLabel.innerHTML = 'On'
      this.motionCheckbox.checked = true
    }
  }

  setContrastState() {
    if (this.options.increaseContrast === true) {
      this.contrastLabel.innerHTML = 'On'
      this.contrastCheckbox.checked = true
    } else {
      this.contrastLabel.innerHTML = 'Off'
      this.contrastCheckbox.checked = false
    }
  }

  setForcedColorsState() {
    if (this.options.forcedColors === true) {
      this.forcedColorsLabel.innerHTML = 'On'
      this.forcedColorsCheckbox.checked = true
    } else {
      this.forcedColorsLabel.innerHTML = 'Off'
      this.forcedColorsCheckbox.checked = false
    }
  }

  openMenu() {
    this.toggleBtn.setAttribute('aria-hidden', true)
    this.toolbar.setAttribute('aria-hidden', false)
    this.toolbar.querySelector('button').focus()

    this.observer.observe(this.toolbar)
  }

  closeMenu(refocus) {
    this.toolbar.setAttribute('aria-hidden', true)
    this.toggleBtn.setAttribute('aria-hidden', false)
    if (refocus !== false) {
      this.toggleBtn.focus()
    }
  }

  updateVisibility() {
    if (document.activeElement.closest('.js-focused')) {
      this.element.style.visibility = 'hidden'
    } else {
      this.element.style.visibility = 'visible'
    }
  }

  registerEventHandlers() {
    document.addEventListener('focusin', () => this.updateVisibility(), true)

    this.toggleBtn.addEventListener('click', () => this.openMenu())
    this.closeBtn.addEventListener('click', () => this.closeMenu())

    document.addEventListener('click', (event) => {
      if (this.toolbar.getAttribute('aria-hidden') === 'false') {
        const withinBoundaries = event.composedPath().includes(this.element)

        if (!withinBoundaries) {
          this.closeMenu(false)
        }
      }
    })

    this.toolbar.addEventListener('focusout', (event) => {
      if (event.relatedTarget !== null && !this.toolbar.contains(event.relatedTarget)) {
        this.closeMenu()
      }
    })

    this.toolbar.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') {
        this.closeMenu()
      }
    })

    this.motionCheckbox.addEventListener('click', () => {
      this.options.reduceMotion = this.toggleButton(this.motionBtn)
      this.setOptions()
      this.setMotionState()
    })

    /* this.contrastCheckbox.addEventListener('click', () => {
      this.options.increaseContrast = this.toggleButton(this.contrastBtn)
      this.setOptions()
      this.setContrastState()
    }) */

    /* this.forcedColorsCheckbox.addEventListener('click', () => {
      this.options.forcedColors = this.toggleButton(this.forcedColorsBtn)
      this.setOptions()
      this.setForcedColorsState()
    }) */

    this.motionQuery.addEventListener('change', () => {
      this.options.reduceMotion = this.motionQuery.matches ? true : undefined
      this.setOptions()
      this.setMotionState()
    })

    this.contrastQuery.addEventListener('change', () => {
      this.options.increaseContrast = this.contrastQuery.matches ? true : undefined
      this.setOptions()
      this.setContrastState()
    })

    this.forcedColorsQuery.addEventListener('change', () => {
      this.options.forcedColors = this.forcedColorsQuery.matches
      this.setOptions()
      this.setForcedColorsState()
    })
  }

  toggleButton(button) {
    return button.getAttribute('aria-checked') === 'false'
  }

  getInitialMotionOption() {
    const cookie = this.getCookie('a11y.reduce_motion')

    if (this.params.has('REDUCE_MOTION')) {
      this.options.reduceMotion = true
    } else if (cookie !== null) {
      this.options.reduceMotion = cookie === 'true'
    } else if (this.motionQuery.matches) {
      this.options.reduceMotion = true
    }
  }

  getInitialContrastOption() {
    // const cookie = this.getCookie('a11y.increase_contrast')

    if (this.params.has('INCREASE_CONTRAST')) {
      this.options.increaseContrast = true
    // } else if (cookie !== null) {
    //   this.options.increaseContrast = cookie === 'true'
    } else if (this.contrastQuery.matches) {
      this.options.increaseContrast = true
    }
  }

  getInitialForcedColorsOption() {
    // const cookie = this.getCookie('a11y.forced_colors_override')

    if (this.params.has('FORCED_COLORS')) {
      this.options.forcedColors = true
    } else if (this.forcedColorsQuery.matches) {
      this.options.forcedColors = true

      // if (cookie !== null) {
      //   this.options.forcedColors = cookie !== 'true'
      // }
    }
  }

  getCookie(name) {
    const re = new RegExp(name + '=([^;]+)')
    const value = re.exec(document.cookie)
    return (value != null) ? unescape(value[1]) : null
  }
}

/**
 * Add module objects alphabetically as needed
 * {
 *  selector: CSS selector for the target element(s)
 *  Source: Imported js class with a constructor. Constructor will be passed
 *      the target DOM element.
 * }
 */
export default {
  selector: SELECTORS.widget,
  Source: A11yWidget
}
